import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import Typography from "@mui/material/Typography";
import type { paths } from "../../api/generated/api-types";
import { getSitesWord } from "./GetSitesWord";

type VerifyRequestSelection =
  paths["/sites/verify"]["post"]["requestBody"]["content"]["application/json"]["selection"];

// The dialog offers every verification selection except "filtered", which is a
// derived mode chosen by the screen (not a radio option). Derived from the
// generated contract so a new backend selection value surfaces here at compile time.
export type VerifyChoice = Exclude<VerifyRequestSelection, "filtered">;

interface VerifyAccessDialogProps {
  open: boolean;
  selectedCredentialCount: number;
  allFilteredSiteCount: number | null;
  onClose: () => void;
  onConfirm: (choice: VerifyChoice) => void;
}

export function VerifyAccessDialog(props: VerifyAccessDialogProps) {
  const { open, selectedCredentialCount, allFilteredSiteCount, onClose, onConfirm } = props;
  const [selection, setSelection] = useState<VerifyChoice>("all");
  const navigate = useNavigate();

  const selectedLabel =
    allFilteredSiteCount !== null
      ? `Выбранные доступы (все ${allFilteredSiteCount} отфильтрованных ${getSitesWord(allFilteredSiteCount)})`
      : `Выбранные доступы (${selectedCredentialCount})`;
  const selectedDisabled = allFilteredSiteCount === null && selectedCredentialCount === 0;

  const handleConfirm = () => {
    onConfirm(selection);
    onClose();
  };

  const handleHistory = () => {
    onClose();
    void navigate("/sites/verifications");
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Проверка доступов</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Выберите какие доступы проверить:
        </Typography>
        <FormControl>
          <RadioGroup
            value={selection}
            onChange={(event) => setSelection(event.target.value as VerifyChoice)}
          >
            <FormControlLabel value="all" control={<Radio />} label="Все доступы" />
            <FormControlLabel
              value="selected"
              control={<Radio />}
              label={selectedLabel}
              disabled={selectedDisabled}
            />
            <FormControlLabel value="unchecked" control={<Radio />} label="Непроверенные" />
            <FormControlLabel value="invalid" control={<Radio />} label="Невалидные" />
          </RadioGroup>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleHistory}>История проверок</Button>
        <Box flexGrow={1} />
        <Button onClick={onClose}>Отмена</Button>
        <Button variant="contained" onClick={handleConfirm}>
          Запустить проверку
        </Button>
      </DialogActions>
    </Dialog>
  );
}
